import { multicall, simulateContract, writeContract } from '@wagmi/core'
import Big from 'big.js'
import { PublicClient, encodeAbiParameters, isAddressEqual, zeroAddress } from 'viem'

import { ConfigBuilderAbi } from 'abis/ConfigBuilderAbi'
import { HomechainOmnichainMessengerAbi } from 'abis/HomechainOmnichainMessagerAbi'
import { IndexV2Abi } from 'abis/IndexV2'
import { notifyTransactionError } from 'components'
import { SuccessPayload } from 'components/widgets/invest-component/storage/invest-states'
import { defaultGasMultiplier, gasMultiplier } from 'services/query-v2/deposit-redeem-v1'
import { TransactionResult } from 'services/query-v2/deposit-redeem-v1/utils'
import { wagmiConfig } from 'services/wallet/chains'
import { convertAmountToWei } from 'utils'

import { ParsedConfigs } from '..'
import { ZeroExAggregator } from '../aggregators/0x-aggregators'
import { deployments } from '../deployments'
import { getPermitData } from '../permit-utils'
import { getRedstonePayload } from '../redstone-utils'

export const depositV2 = async (
  client: PublicClient,
  configs: ParsedConfigs,
  props: SuccessPayload,
) => {
  let response: TransactionResult | undefined
  const {
    inputCurrency,
    account,
    sellAmount,
    outputCurrency,
    permit,
    buyAmount,
    stopProcessing,
    clearState,
    isUserContract,
  } = props

  const { ConfigBuilder, Messenger } = deployments[outputCurrency.chainId][outputCurrency.address]

  const contract = {
    address: ConfigBuilder,
    abi: ConfigBuilderAbi,
  }

  try {
    const [[depositConfig], { staticPriceOracle }] = await multicall(wagmiConfig, {
      contracts: [
        {
          ...contract,
          functionName: 'configs',
          args: [configs.config],
        },
        {
          ...contract,
          functionName: 'getState',
        },
      ],
      allowFailure: false,
    })

    const amount = BigInt(convertAmountToWei(sellAmount, inputCurrency.decimals).toFixed())
    const depositParams = {
      config: depositConfig,
      amount,
      recipient: account,
      payload: await getRedstonePayload.fromContract(staticPriceOracle),
      // payload: await getRedstonePayload(wagmiConfig, { PriceOracle: staticPriceOracle }),
    } as const

    const isReserve = isAddressEqual(inputCurrency.address, configs.reserveAsset)
    if (inputCurrency.type === 'native' && isReserve) {
      if (stopProcessing) stopProcessing()
      const { request } = await simulateContract(wagmiConfig, {
        address: outputCurrency.address,
        abi: IndexV2Abi,
        functionName: 'deposit',
        args: [depositParams, zeroAddress, '0x'],
        value: amount,
        stateOverride: [
          {
            address: account,
            balance: amount,
          },
        ],
      })

      const gas = await client.estimateContractGas(request)
      const scaledGas =
        (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000)

      const hash = await writeContract(wagmiConfig, {
        ...request,
        gas: scaledGas,
      })

      if (clearState) clearState()
      response = {
        hash: hash,
        params: props,
        buyAmount: Big(buyAmount ? buyAmount.toFixed(3) : 0),
      } as TransactionResult
      return response
    }
    if (isReserve) {
      if (stopProcessing) stopProcessing()

      const { request } = await simulateContract(wagmiConfig, {
        address: Messenger,
        abi: HomechainOmnichainMessengerAbi,
        functionName: permit === undefined ? 'deposit' : 'depositWithPermit',
        args:
          permit === undefined
            ? [inputCurrency.address, amount, depositParams]
            : [
                inputCurrency.address,
                amount,
                depositParams,
                isUserContract ? '0x' : await getPermitData(account, Messenger, amount, permit),
              ],
      })

      const gas = await client.estimateContractGas(request)
      const scaledGas =
        (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000)

      const hash = await writeContract(wagmiConfig, {
        ...request,
        gas: scaledGas,
      })
      if (clearState) clearState()
      response = {
        hash: hash,
        params: props,
        buyAmount: Big(buyAmount ? buyAmount.toFixed(3) : 0),
      } as TransactionResult
      return response
    }

    const aggregator = ZeroExAggregator.builder
      .withMaxRetries(5)
      .withShouldSellEntireBalance(false)
      .build()

    const trade = await aggregator.quote(
      inputCurrency.chainId,
      inputCurrency.address,
      configs.reserveAsset,
      amount,
      outputCurrency.address,
    )

    if (stopProcessing) stopProcessing()

    const { request } = await simulateContract(wagmiConfig, {
      address: Messenger,
      abi: HomechainOmnichainMessengerAbi,
      functionName: 'depositAndTradeWithPermit',
      value: isAddressEqual(inputCurrency.address, zeroAddress) ? amount : undefined,
      args: [
        inputCurrency.address,
        amount,
        depositParams,
        permit && !isUserContract ? await getPermitData(account, Messenger, amount, permit) : '0x',
        encodeAbiParameters(
          [
            {
              type: 'tuple',
              components: [
                { name: 'target', type: 'address' },
                { name: 'allowanceTarget', type: 'address' },
                { name: 'data', type: 'bytes' },
              ],
            },
          ],
          [{ target: trade.to, allowanceTarget: trade.allowanceTarget, data: trade.data }],
        ),
      ],
    })

    const gas = await client.estimateContractGas(request)
    const scaledGas =
      (gas * (gasMultiplier[inputCurrency.chainId] ?? defaultGasMultiplier)) / BigInt(10000)

    const hash = await writeContract(wagmiConfig, {
      ...request,
      gas: scaledGas,
    })
    if (clearState) clearState()
    response = {
      hash: hash,
      params: props,
      buyAmount: Big(buyAmount ? buyAmount.toFixed(3) : 0),
    } as TransactionResult
  } catch (e) {
    // TODO: types & messages
    // const event = e as WriteContractErrorType
    // console.log("shortMessage" in error ? error.shortMessage : "error")
    notifyTransactionError()
  }

  return response
}
